/** Dependencies */
import angular from 'angular';
/** Templates */
import kpsPaymentDialogTemplate from './kpsPaymentDialog.html';

export const kpsPayComponent = {
  template: kpsPaymentDialogTemplate,
  bindings: { modalInstance: '<', resolve: '<' },
  controller: kpsPayController
};

export function KpsPaymentDialogService($uibModal) {
  this.show = function (
    paymentSessionId,
    kpsIframeUrl,
    completePayment,
    handlePaymentFail,
    createKpsEventLog,
    updateKpsEventLog
  ) {
    return $uibModal.open({
      backdrop: 'static',
      component: 'kpsPay',
      resolve: {
        paymentSessionId: () => paymentSessionId,
        kpsIframeUrl: () => kpsIframeUrl,
        handlePaymentFail: () => handlePaymentFail,
        completePayment: () => completePayment,
        createKpsEventLog: () => createKpsEventLog,
        updateKpsEventLog: () => updateKpsEventLog,
        /***
         * With the old file based routing the iframe url
         * does not always directly translate to kpsHost.
         *
         * Ex: https://secure.devkevpayments.com/capture
         * Ex: https://checkout.kevpayments.com/iframe
         ***/
        kpsHost: () =>
          kpsIframeUrl.replace(/\/capture/, '').replace(/\/iframe/, '')
      },
      size: 'md'
    }).result;
  };
}

function kpsPayController($scope, $injector, $window) {
  const $ctrl = this;

  const $svcs = { $ctrl, $scope };

  $ctrl.$onInit = function () {
    $injector.invoke(initCtrl, null, Object.assign($svcs, { $svcs }));

    // Add the event listener
    angular.element($window).on('message', $ctrl.onResize);
    angular.element($window).on('message', $ctrl.messageWindowListener);
    angular.element($ctrl.payframeElement).on('load', $ctrl.loadHandler);

    // Clean up the listener when the controller is destroyed
    $scope.$on('$destroy', function () {
      angular.element($window).off('message', $ctrl.onResize);
      angular.element($window).off('message', $ctrl.messageWindowListener);
      angular.element($ctrl.payframeElement).off('load', $ctrl.loadHandler);
    });
  };
}

/* @ngInject */ function initCtrl($ctrl, $injector, $svcs) {
  $ctrl.payframeElement = document.getElementById('kps-payment-frame');

  $ctrl.payframeElement.setAttribute(
    'src',
    `${$ctrl.resolve.kpsIframeUrl}?sessionId=${$ctrl.resolve.paymentSessionId}`
  );
  $ctrl.payframeElement.setAttribute('width', '100%');
  $ctrl.payframeElement.setAttribute('height', '550px');

  Object.assign($ctrl, {
    // Injector fns
    loadHandler: $injector.invoke(loadHandler, null, $svcs),
    messageWindowListener: $injector.invoke(messageWindowListener, null, $svcs),
    onResize: $injector.invoke(onResize, null, $svcs),
    kpsIframeUrl: $ctrl.resolve.kpsIframeUrl,
    kpsHost: $ctrl.resolve.kpsHost
  });
}

/* @ngInject */ function onResize($scope, $ctrl) {
  return function (event) {
    if (
      event.originalEvent &&
      event.originalEvent.data &&
      event.originalEvent.data.type === 'resize' &&
      event.originalEvent.data.height
    ) {
      $ctrl.payframeElement.setAttribute(
        'height',
        // I add 40px to the height to account for the padding on the bottom of the iframe in our styles.
        event.originalEvent.data.height + 40 + 'px'
      );
      $scope.$apply(); // Update scope
    }
  };
}

/* @ngInject */ function loadHandler(BusyScreen) {
  return function () {
    BusyScreen.setBusy(false);
  };
}

/**
 * We want to identify what browser the user is on. Unfortunately, the useragent API doesn't directly
 * say "I am Chrome" or "I am Firefox". Instead, we have to parse the userAgent string ourselves.
 *
 * Example - Edge returns back:
 *    Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/139.0.0.0 Safari/537.36 Edg/139.0.0.0"
 * @returns string browser
 */
function detectBrowser() {
  const userAgent = navigator.userAgent;
  if (userAgent.includes('Firefox')) {
    return 'Firefox';
  } else if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
    return 'Chrome';
  } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    return 'Safari';
  } else if (userAgent.includes('Edg')) {
    return 'Edge';
  } else if (userAgent.includes('Opera') || userAgent.includes('OPR')) {
    return 'Opera';
  } else if (userAgent.includes('MSIE') || userAgent.includes('Trident')) {
    return 'IE';
  } else {
    return 'Other';
  }
}

/**
 * the pathname gives me something like this: "/(S(epukiu4jcb401ijnd4fgrtno))/index.aspx"
 * and we use that to grab the session id.
 * @returns string sessionId
 */
function grabSessionId() {
  const match = document.location.pathname.match(/\(S\(([^)]+)\)\)/);
  return match ? match[1] : null;
}

/**
 * Checks the platform the user is on. Due to Safari and Firefox not supporting the userAgentData API,
 * we default to checking the userAgent string if the userAgentData API is not available.
 *
 * @returns
 */
function getPlatform() {
  if (navigator.userAgentData && navigator.userAgentData.platform) {
    return navigator.userAgentData.platform;
  }

  const ua = navigator.userAgent;
  if (/iPad/.test(ua)) return 'iPadOS';
  if (/iPhone|iPod/.test(ua)) return 'iOS';
  if (/Macintosh/.test(ua)) return 'macOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Windows/.test(ua)) return 'Windows';
  return 'Unknown';
}

/**
 * Checks if the users device is a mobile device.
 * Due to Safari and Firefox not supporting the userAgentData API, we default to checking the userAgent string
 * if the userAgentData API is not available.
 *
 * @returns
 */
function isMobileDevice() {
  if (navigator.userAgentData && navigator.userAgentData.mobile) {
    return navigator.userAgentData.mobile;
  }

  // Fallback for Safari/Firefox
  const ua = navigator.userAgent;
  if (/Mobi|Android|iPhone|iPod/i.test(ua)) return true;
  if (/iPad/.test(ua)) return false; // treat iPad as non-mobile
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return false; // iPad pretending as Mac
  return false;
}

/**
 * Validates if a postMessage event is a legitimate KPS message by checking multiple criteria:
 * 1. Origin matches the expected KPS host
 * 2. Source window is from the KPS payment iframe
 * 3. Message structure contains expected KPS message types
 * 4. Event is trusted by the browser
 *
 * This helps filter out unwanted messages from browser extensions, ads, and other scripts
 * that may interfere with the payment process logging.
 *
 * @param {MessageEvent} event - The postMessage event to validate
 * @param {string} kpsHost - The expected KPS host origin
 * @param {HTMLIFrameElement} payframeElement - The KPS payment iframe element
 * @returns {boolean} - True if the message is valid and from KPS
 */
function isValidKpsMessage(event, kpsHost, payframeElement) {
  // Check if event exists and has the expected structure
  if (!event || !event.originalEvent) {
    return false;
  }

  const originalEvent = event.originalEvent;

  // Verify the message origin matches the expected KPS host
  if (originalEvent.origin !== kpsHost) {
    return false;
  }

  // Verify the payframe element exists and has a content window
  // This prevents errors if the iframe hasn't been rendered yet or was removed
  if (!payframeElement || !payframeElement.contentWindow) {
    return false;
  }

  // Verify the message source is from our payment iframe
  if (originalEvent.source !== payframeElement.contentWindow) {
    return false;
  }

  // Verify the event is trusted (not programmatically created)
  if (!originalEvent.isTrusted) {
    return false;
  }

  // Check if the message has the expected data structure
  if (!originalEvent.data || typeof originalEvent.data !== 'object') {
    return false;
  }

  console.log(originalEvent.data);

  // Verify the message contains a valid KPS message type
  const validKpsMessageTypes = [
    'payment',
    'status',
    'resize',
    'cancel',
    'error'
  ];
  const messageType = originalEvent.data.type;

  if (
    !messageType ||
    !validKpsMessageTypes.includes(messageType.toLowerCase())
  ) {
    return false;
  }

  return true;
}

/**
 * Gathers a list of data that might be useful for us to debug an issue that seems to be happening to
 * our WebStore users when they click "Check out". When it is trying to load up the KPS Iframe with
 * the payment input form, it fails to reach that point and gets stuck on the loader within the iframe.
 * However, we do not know exactly why this is happening as it seems to be intermittent. We also do
 * not know 100%, if its an issue of the user, issue of our code or issue with KPS or so on.
 *
 * Therefore, we are gathering basic information about the user, whats in their cart, whats their
 * browser environment, are the KPS hosts the iframe is calling correct, when this is happening and
 * so on.
 *
 * This is the ticket associated with this issue: https://graystep.atlassian.net/browse/GR-2098
 */
class KpsIframeEventLog {
  constructor(paymentSessionID, kpsHost) {
    this.id = null;
    this.orgID = null;
    // KPS detail
    this.paymentSessionID = paymentSessionID;
    this.kpsHost = kpsHost;
    this.isTrustedByKps = null;
    this.kpsResponse = null;
    // User Detail
    this.username = null;
    this.ip = null;
    this.sessionId = this._safeExecute(
      () => grabSessionId(),
      'sessionId',
      null
    );
    this.url = this._safeExecute(
      () => document.location.href,
      'url',
      'Unknown'
    );
    this.cart = this._safeExecute(
      () => localStorage.getItem('cart'),
      'cart',
      null
    );
    // Browser and System information
    this.platform = this._safeExecute(
      () => getPlatform(),
      'platform',
      'Unknown'
    );
    this.browser = this._safeExecute(
      () => detectBrowser(),
      'browser',
      'Unknown'
    );
    this.isMobile = this._safeExecute(() => isMobileDevice(), 'isMobile', null);
    this.language = this._safeExecute(
      () => window.navigator.language,
      'language',
      'Unknown'
    );
    this.hasCookieEnabled = this._safeExecute(
      () => window.navigator.cookieEnabled,
      'hasCookieEnabled',
      null
    );
    // Basic flag
    this.tries = 0;
    this.status = 0;
    this.timestamp = new Date().toISOString();
  }

  /**
   * This allows us to take the log that was returned after saving to the database and update
   * our local object with the results. This is important because the id, orgID, username and
   * ip are only added by the server when the record is created. Therefore, we want to make sure
   * that our local object is updated with those values as well, mainly the ID. The other values
   * are not as important since they are already stored. The ID determines if we create a new
   * record or update an existing one.
   * @param {*} savedLog
   */
  updateLogWithDBResults(savedLog) {
    Object.assign(this, savedLog);
  }

  /**
   * Helps to handle the kpsResponse data that comes back from KPS. We want to make sure
   * to concatenate the data we get back instead of overwriting it, so that we have
   * a full log of everything that was sent back to us from KPS for that session. Allowing
   * us to see the full picture of what happened and what lead to it. The reason we also need
   * to stringify the data is because the response from KPS is an object. An example of it:
   * {"type":"resize","height":36}. With multiple responses gathered, it will look like this
   * is our database: {"type":"status","message":"load-start"}, {"type":"status","message":"load-complete"}
   *
   * @param {any} event
   * @returns void
   */
  handleKpsResponse(event) {
    if (event.originalEvent.data.type === 'resize') {
      // we dont care to fill up our db rows with these
      return;
    }

    // Add a timestamp to the event for tracking
    event.originalEvent.data.time = new Date().toLocaleTimeString();

    // If a previous response exists, concatenate the new data to it
    if (this.kpsResponse) {
      const dataResponseFromKps = JSON.stringify(event.originalEvent.data);
      const concatenatedResponse = this.kpsResponse.concat(
        ', ',
        dataResponseFromKps
      );
      this.kpsResponse = concatenatedResponse;
      return;
    } else {
      // we know event.originalEvent.data exists because we validated it before calling this function
      this.kpsResponse = JSON.stringify(event.originalEvent.data);
    }
  }

  /**
   * Safely executes a function and handles any errors by logging them and returning a fallback value.
   * This will ensure for any cases where we use a function or API, we don't bubble and break the
   * page and instead just logs out to the screen and returns a fallback value allowing us to continue.
   *
   * @param {Function} fn - The function to execute
   * @param {string} propertyName - The name of the property being set (for logging)
   * @param {any} fallbackValue - The fallback value to return if execution fails
   * @returns {any} - The result of the function or the fallback value
   */
  _safeExecute(fn, propertyName, fallbackValue) {
    try {
      return fn();
    } catch (error) {
      console.error(`KpsIframeEventLog: Error getting ${propertyName}:`, error);
      return fallbackValue;
    }
  }
}

/* @ngInject */ function messageWindowListener($ctrl, Alert, CartService) {
  // Add a variable to hold the interval ID
  let loadingIntervalId = null;
  let pendingUpdate = false;

  /**
   * This record will strictly be used to record the events of when the loader appears on the
   * KPS iframe itself and takes longer than the expected time interval.
   */
  const kpsIframeEventLog = new KpsIframeEventLog(
    $ctrl.resolve.paymentSessionId,
    $ctrl.resolve.kpsHost
  );

  /**
   * Updates the server log with the current state of the kpsIframeEventLog. If the log has not been created
   * in the database yet (i.e. it does not have an ID), then we set a flag to indicate that an update is pending.
   * Once the log is created and we have an ID, we will check if there is a pending update and if so,
   * we will call this function again to update the log with the latest information.
   */
  function updateServerLog() {
    if (!kpsIframeEventLog.id) {
      pendingUpdate = true;
      return;
    }

    $ctrl.resolve.updateKpsEventLog(kpsIframeEventLog).catch((error) => {
      console.error('Error updating KPS Event Log:', error);
    });
  }

  /**
   * Start logging the data we need every 5 seconds when the listener is first set up.
   * Its important that this lives outside of the returned function below, because it
   * makes it so that its only initialized once, when the function is first set up.
   * We limit the amount of times we log this data to 5 times, to prevent excessive logging
   * to our database. If the user refreshes the page, then this will re-initialize and start
   * the logging again.
   */
  if (!loadingIntervalId) {
    // Initial log to create the record in the database
    $ctrl.resolve
      .createKpsEventLog(kpsIframeEventLog)
      .then((savedLog) => {
        kpsIframeEventLog.updateLogWithDBResults(savedLog);
        if (pendingUpdate) {
          pendingUpdate = false;
          updateServerLog();
        }
      })
      .catch((error) =>
        console.error('Error logging KPS iframe event:', error)
      );

    loadingIntervalId = setInterval(() => {
      /**
       * If the id exists, then we know the record has already been created in the database,
       * and we have updated the local constant, so we just want to update the record in the
       * db with the new information we have gathered. Otherwise, we create a new record in the
       * db with the information we have gathered.
       */
      if (kpsIframeEventLog.id) {
        $ctrl.resolve
          .updateKpsEventLog(kpsIframeEventLog)
          .then(() => {
            kpsIframeEventLog.tries++;
          })
          .catch((error) =>
            console.error('Error updating logs for KPS iframe event:', error)
          );
      }

      if (kpsIframeEventLog.tries > 10) {
        clearInterval(loadingIntervalId);
        loadingIntervalId = null;
      }
    }, 5000);
  }

  return function (event) {
    // Use comprehensive validation instead of simple origin check
    if (!isValidKpsMessage(event, $ctrl.kpsHost, $ctrl.payframeElement)) {
      return;
    }

    const isTrusted =
      (event.originalEvent && event.originalEvent.isTrusted) || null;

    /******************************* Event Log *********************************/
    kpsIframeEventLog.isTrustedByKps = isTrusted;
    kpsIframeEventLog.handleKpsResponse(event);

    switch (
      event.originalEvent.data.type &&
      event.originalEvent.data.type.toLowerCase()
    ) {
      case 'payment': {
        if (event.originalEvent.data.success) {
          /***
           * We avoid a race condition but using a finally here to make sure that we wait for
           * complete payment to finish processing before we dismiss the modal and potentially remove the iframe from the DOM. If we remove
           * iframe too soon then complete payment would not exist and therefore never file.
           */
          $ctrl.resolve
            .completePayment()
            .finally(() => $ctrl.modalInstance.dismiss());
        } else {
          Alert.error('The payment failed to be processed!');
        }

        updateServerLog();
        break;
      }
      case 'status': {
        if (event.originalEvent.data.message === 'load-complete') {
          clearInterval(loadingIntervalId);
          loadingIntervalId = null;
        }

        updateServerLog();
        break;
      }
      case 'cancel': {
        updateServerLog();
        $ctrl.modalInstance.dismiss();
        break;
      }
      case 'resize': {
        break;
      }
      case 'error': {
        updateServerLog();
        break;
      }
      default: {
        updateServerLog();
        break;
      }
    }
  };
}
